import React from "react";
import { Formik, Form, Field } from "formik";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import * as Yup from "yup";
import logo from "../../resources/icons/logo.png";
import { useAuthContext } from "../../contexts/authContext";

interface LoginValues {
  email: string;
  password: string;
}

const initialValues: LoginValues = {
  email: "",
  password: "",
};

const validationSchema = Yup.object({
  email: Yup.string()
    .email("Please enter a valid email")
    .required("Email is required"),
  password: Yup.string().required("Password is required"),
});

const AdminLogin: React.FC = () => {
  const navigate = useNavigate();
  const { setUser } = useAuthContext();

  const onSubmit = async (
    values: LoginValues,
    {
      setSubmitting,
      setFieldError,
    }: {
      setSubmitting: (isSubmitting: boolean) => void;
      setFieldError: (field: string, message: string) => void;
    }
  ) => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_URL}/admin/login`,
        values,
        {
          headers: {
            "X-Client-Type": "web",
          },
        }
      );
      const { token, user } = res.data;

      if (user.type !== "ADMIN") {
        setFieldError("email", "Only admins can log in here.");
        return;
      }

      localStorage.setItem("token", token);
      localStorage.setItem("userType", user.type);
      setUser(user);

      navigate("/admin/management");
    } catch (error: any) {
      const message = error?.response?.data?.error;

      if (message === "User not found" || error?.response?.status === 404) {
        setFieldError("email", "No admin account found with this email.");
      } else if (
        message === "Incorrect password" ||
        error?.response?.status === 401
      ) {
        setFieldError("password", "Incorrect password. Please try again.");
      } else {
        setFieldError("password", message || "Login failed.");
      }

      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-row mx-auto min-h-screen">
      <div
        className="w-[40rem] flex flex-col items-center justify-center"
        style={{
          background: "linear-gradient(38.32deg, #1A2835 65%, #2B4A61 35%)",
        }}
      >
        <img src={logo} alt="Logo" className="mt-[40%] max-w-full h-16" />
        <p className="mt-4 text-3xl text-center text-white font-bold">
          Build Connect
        </p>
        <p className="mt-[12rem] text-xs text-center text-white">
          Connecting clients and construction companies to develop better
          together.
        </p>
      </div>

      <div className="w-[50rem] pt-16 flex flex-col justify-center">
        <div className="w-full max-w-[25rem] mx-auto">
          <h2 className="mb-4 text-[32px]">Admin Login</h2>
          <p className="mb-8 text-gray-500">
            Only authorized admins can log in here.
          </p>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {({ isSubmitting, errors }) => (
              <Form>
                <div className="mb-6">
                  <label htmlFor="email" className="block mb-2 font-bold">
                    Email
                  </label>
                  <Field
                    id="email"
                    name="email"
                    type="text"
                    placeholder="Enter your email"
                    className={`w-full p-3 border ${
                      errors.email ? "border-red-500" : "border-gray-300"
                    } rounded text-base`}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                  )}
                </div>

                <div className="mb-6">
                  <label htmlFor="password" className="block mb-2 font-bold">
                    Password
                  </label>
                  <Field
                    id="password"
                    name="password"
                    type="password"
                    placeholder="Enter your password"
                    className={`w-full p-3 border ${
                      errors.password ? "border-red-500" : "border-gray-300"
                    } rounded text-base`}
                  />
                  {errors.password && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.password}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="h-10 w-full p-3 bg-[#1672ec] text-white rounded text-base hover:bg-[#1657b3]"
                >
                  {isSubmitting ? "Logging in..." : "Login"}
                </button>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
